import { useState } from "react";
import axios from "axios";
import { CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreateCustomerForm = ({ onSuccess }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [loading, setLoading] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const token = localStorage.getItem("token");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/customers`,
        { name, email, phone, address, city },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTempPassword(res.data.temp_password);
      onSuccess(res.data.customer);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create customer");
      setLoading(false);
    }
  };

  if (tempPassword) {
    return (
      <div className="space-y-4 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <CheckCircle className="w-8 h-8 text-green-500" />
        </div>
        <h3 className="font-bold text-lg text-slate-900">Customer Created!</h3>
        <p className="text-slate-600">A welcome email has been sent to <strong>{email}</strong></p>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-slate-600 mb-2">Temporary Password (if email fails):</p>
          <p className="font-mono text-lg font-bold text-amber-600 bg-white px-3 py-2 rounded border">{tempPassword}</p>
        </div>
        <p className="text-xs text-slate-500">Please share this password securely with the customer.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-widest">Name *</Label>
        <Input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Customer name"
          className="h-10"
          required
          data-testid="new-customer-name"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-widest">Email *</Label>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="customer@email.com"
          className="h-10"
          required
          data-testid="new-customer-email"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-widest">Phone</Label>
        <Input
          type="text"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+1234567890"
          className="h-10"
          data-testid="new-customer-phone"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest">Address</Label>
          <Input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="123 Main St"
            className="h-10"
            data-testid="new-customer-address"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest">City</Label>
          <Input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="City"
            className="h-10"
            data-testid="new-customer-city"
          />
        </div>
      </div>
      <p className="text-xs text-slate-500">A temporary password will be generated and emailed to the customer.</p>
      <Button
        type="submit"
        disabled={loading}
        className="w-full h-10 bg-blue-500 hover:bg-blue-600 text-white font-bold uppercase tracking-widest"
        data-testid="create-customer-btn"
      >
        {loading ? "Creating..." : "Create Customer"}
      </Button>
    </form>
  );
};

export default CreateCustomerForm;
